import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { TRANSITIONS, TRANSITION_ENQUIRE, TRANSITION_SEND_CALENDAR_LINK, TRANSITIONS_OLD } from '../../util/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { filterTxsForRfq } from './utils';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_ALL_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ALL_ORDERS_OR_SALES_REQUEST';
export const FETCH_ALL_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ALL_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ALL_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ALL_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],

  fetchAllInProgress: false,
  fetchAllOrdersOrSalesError: null,
  transactionAllRefs: [],
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };


    case FETCH_ALL_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchAllInProgress: true, fetchAllOrdersOrSalesError: null };
    case FETCH_ALL_ORDERS_OR_SALES_SUCCESS: {
      const transactionsAll = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchAllInProgress: false,
        transactionAllRefs: entityRefs(transactionsAll),
      };
    }
    case FETCH_ALL_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchAllInProgress: false, fetchAllOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});


const fetchAllOrdersOrSalesRequest = () => ({ type: FETCH_ALL_ORDERS_OR_SALES_REQUEST });
const fetchAllOrdersOrSalesSuccess = response => ({
  type: FETCH_ALL_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchAllOrdersOrSalesError = e => ({
  type: FETCH_ALL_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const fetchAllTransaction = () => (dispatch, getState, sdk) => {
  dispatch(fetchAllOrdersOrSalesRequest());

  const apiQueryParams = {
    lastTransitions: [...TRANSITIONS, TRANSITION_SEND_CALENDAR_LINK, ...TRANSITIONS_OLD],
    include: [
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'protectedData',
      'metadata',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],    
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      // const finalResponse = filterTxsForRfq(response, isOnlyMessages);
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchAllOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchAllOrdersOrSalesError(storableError(e)));
      throw e;
    });
}

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order', //messages
    sales: 'sale', //rfq
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  const isOnlyMessages = tab === 'orders';
  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    // only: onlyFilter,
    lastTransitions: tab === "orders" ? TRANSITION_ENQUIRE : [TRANSITIONS, ...TRANSITIONS_OLD].filter(item => item !== TRANSITION_ENQUIRE),
    include: [
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'protectedData',
      'metadata'
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    per_page: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      // const finalResponse = filterTxsForRfq(response, isOnlyMessages);

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      dispatch(fetchCurrentUserNotifications());
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
